import React, { useState } from 'react';
import { usePracticePulse } from '../../hooks/usePracticePulse';
import { FeedCard } from './FeedCard';
import { FeedEmptyState } from './FeedEmptyState';
import { FeedFilters } from './FeedFilters';
import './PulseFeed.css';

/**
 * PulseFeed — social activity and news feed with glassmorphic cards.
 */
export const PulseFeed = ({
  mode = 'full',
  maxHeight = null,
  showFilters = true,
  showHeader = true,
  language = 'he',
  onItemClick,
  onAIAlertClick,
  onNavigate,
}) => {
  const [activeFilters, setActiveFilters] = useState({});
  const isHebrew = language === 'he';

  const {
    items,
    loading,
    error,
    lastUpdated,
    hasMore,
    hasAIAlerts,
    aiAlertCount,
    refresh,
    loadMore,
    toggleLike,
    toggleBookmark,
  } = usePracticePulse({ filters: activeFilters });

  const handleFilterChange = (filters) => {
    setActiveFilters(filters);
  };

  const handleItemClick = (item) => {
    // Custom handler takes priority
    if (onItemClick) { onItemClick(item); return; }

    // AI alert specific handler
    if (item.source === 'ai_alert' && onAIAlertClick) {
      onAIAlertClick(item);
      return;
    }

    // External links open in new tab
    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Internal navigation via onNavigate callback
    if (!onNavigate) return;

    switch (item.source) {
      case 'ai_alert':
        onNavigate('ai-hub');
        break;
      case 'practice_activity':
        if (item.category === 'appointment') onNavigate('calendar');
        else if (item.category === 'patient') onNavigate('patients');
        else if (item.category === 'billing') onNavigate('billing');
        else if (item.category === 'inventory') onNavigate('inventory');
        else onNavigate('calendar');
        break;
      case 'clinical_win':
        onNavigate('reports');
        break;
      default:
        break;
    }
  };

  const formatLastUpdated = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);

    if (diffMins < 1) return isHebrew ? '\u05E2\u05DB\u05E9\u05D9\u05D5' : 'Just now';
    if (diffMins < 60) return isHebrew ? `\u05DC\u05E4\u05E0\u05D9 ${diffMins} \u05D3\u05E7\u05F3` : `${diffMins}m ago`;
    return date.toLocaleTimeString(isHebrew ? 'he-IL' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={`pulse-feed pulse-feed-${mode}`}>
      {showHeader && (
        <header className="pulse-feed-header">
          <div className="pulse-feed-title-row">
            <h3 className="pulse-feed-title">
              <span className="pulse-dot" />
              {isHebrew ? '\u05E4\u05E2\u05D9\u05DC\u05D5\u05EA \u05D1\u05D6\u05DE\u05DF \u05D0\u05DE\u05EA' : 'Live Feed'}
            </h3>
            {hasAIAlerts && (
              <span className="ai-alert-badge">
                {'\uD83E\uDD16'} {aiAlertCount}
              </span>
            )}
          </div>

          <div className="pulse-feed-meta">
            <span className="last-updated">{formatLastUpdated(lastUpdated)}</span>
            <button
              className="pulse-refresh-btn"
              onClick={refresh}
              disabled={loading}
              title={isHebrew ? '\u05E8\u05E2\u05E0\u05DF' : 'Refresh'}
            >
              {'\uD83D\uDD04'}
            </button>
          </div>
        </header>
      )}

      {showFilters && mode === 'full' && (
        <FeedFilters
          activeFilters={activeFilters}
          onChange={handleFilterChange}
          language={language}
        />
      )}

      <div
        className="pulse-feed-content"
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : {}}
      >
        {loading && items.length === 0 ? (
          <div className="pulse-feed-loading">
            <div className="pulse-loading-spinner" />
            <span>{isHebrew ? '\u05D8\u05D5\u05E2\u05DF...' : 'Loading...'}</span>
          </div>
        ) : error ? (
          <div className="pulse-feed-error">
            <span>{'\u26A0\uFE0F'}</span>
            <p>{isHebrew ? '\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05D4\u05E4\u05D9\u05D3' : 'Error loading feed'}</p>
            <button onClick={refresh} className="btn-retry">
              {isHebrew ? '\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1' : 'Retry'}
            </button>
          </div>
        ) : items.length === 0 ? (
          <FeedEmptyState language={language} />
        ) : (
          <>
            <ul className="feed-items-list">
              {items.map((item, index) => (
                <li key={item.id} className="feed-item-wrapper">
                  <FeedCard
                    item={item}
                    mode={mode}
                    language={language}
                    onClick={() => handleItemClick(item)}
                    onLike={() => toggleLike(item.id)}
                    onBookmark={() => toggleBookmark(item.id)}
                    isNew={index < 3 && !loading}
                  />
                </li>
              ))}
            </ul>

            {hasMore && (
              <button
                className="load-more-btn"
                onClick={loadMore}
                disabled={loading}
              >
                {loading
                  ? (isHebrew ? '\u05D8\u05D5\u05E2\u05DF...' : 'Loading...')
                  : (isHebrew ? '\u05D8\u05E2\u05DF \u05E2\u05D5\u05D3' : 'Load More')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
